/**
 * Universal Scoring Color System for halcy.ai
 *
 * This module defines a consistent 4-tier color hierarchy used across
 * all scoring components (ScoreGauge, DiagnosticSection, etc.)
 *
 * ## Tier Mapping
 *
 * | Tier      | Grade | Score Range | Color   | Use Case                    |
 * |-----------|-------|-------------|---------|----------------------------|
 * | Excellent | A     | 90-100%     | Emerald | Achievement, top performer |
 * | Good      | B     | 70-89%      | Teal    | Solid, minor gaps          |
 * | Fair      | C/D   | 40-69%      | Amber   | Needs improvement          |
 * | Poor      | F     | 0-39%       | Red     | Needs urgent attention     |
 *
 * ## Integration
 *
 * - Use `gradeToTier()` to convert letter grades to tiers
 * - Use `percentageToTier()` for 0-100 percentage scores
 * - For a component score out of a variable max, pass `(score / max) * 100`
 *   to `percentageToTier()` – the one set of tier boundaries
 *
 * @see ai-visibility-checker/scoring.ts for grade calculation logic
 * @see GRADE_THRESHOLDS for the threshold configuration
 */

// =============================================================================
// Types
// =============================================================================

export type ScoreTier = 'excellent' | 'good' | 'fair' | 'poor';

export type Grade = 'A' | 'B' | 'C' | 'D' | 'F';

export interface TierColors {
  /** SVG stroke color (hex) */
  stroke: string;
  /** Background fill with opacity */
  fill: string;
  /** Tailwind text color class */
  text: string;
  /** Text color as hex (for SVG) */
  textHex: string;
  /** Tailwind background class */
  bg: string;
  /** Tailwind border class */
  border: string;
  /** Tailwind progress bar fill class (solid color) */
  progressFill: string;
  /** Glow/shadow color with opacity */
  glow: string;
  /** Human-readable label */
  label: string;
}

// =============================================================================
// Color Constants
// =============================================================================

/**
 * Universal tier colors using Tailwind's default palette.
 *
 * Color choices:
 * - Emerald: Success, achievement (universal "good" color)
 * - Teal: Positive but distinct from emerald (visual hierarchy)
 * - Amber: Caution, needs improvement (universal "warning" color)
 * - Red: Attention needed, action required (universal "alert" color)
 */
export const SCORE_TIER_COLORS: Record<ScoreTier, TierColors> = {
  excellent: {
    stroke: '#10B981', // emerald-500
    fill: 'rgb(16 185 129 / 0.1)',
    text: 'text-emerald-600',
    textHex: '#059669', // emerald-600
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    progressFill: 'bg-emerald-500',
    glow: 'rgba(16, 185, 129, 0.25)',
    label: 'Excellent',
  },
  good: {
    stroke: '#14B8A6', // teal-500
    fill: 'rgb(20 184 166 / 0.1)',
    text: 'text-teal-600',
    textHex: '#0D9488', // teal-600
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    progressFill: 'bg-teal-500',
    glow: 'rgba(20, 184, 166, 0.25)',
    label: 'Good',
  },
  fair: {
    stroke: '#F59E0B', // amber-500
    fill: 'rgb(245 158 11 / 0.1)',
    text: 'text-amber-600',
    textHex: '#D97706', // amber-600
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    progressFill: 'bg-amber-500',
    glow: 'rgba(245, 158, 11, 0.25)',
    label: 'Needs Improvement',
  },
  poor: {
    stroke: '#EF4444', // red-500
    fill: 'rgb(239 68 68 / 0.1)',
    text: 'text-red-600',
    textHex: '#DC2626', // red-600
    bg: 'bg-red-50',
    border: 'border-red-200',
    progressFill: 'bg-red-500',
    glow: 'rgba(239, 68, 68, 0.2)',
    label: 'Needs Attention',
  },
} as const;

// =============================================================================
// Mapping Functions
// =============================================================================

/**
 * Map a letter grade to a score tier.
 */
export function gradeToTier(grade: Grade): ScoreTier {
  switch (grade) {
    case 'A':
      return 'excellent';
    case 'B':
      return 'good';
    case 'C':
    case 'D':
      return 'fair';
    case 'F':
      return 'poor';
  }
}

/**
 * Map a percentage score (0-100) to a score tier.
 */
export function percentageToTier(score: number): ScoreTier {
  if (score >= 90) return 'excellent';
  if (score >= 70) return 'good';
  if (score >= 40) return 'fair';
  return 'poor';
}

/**
 * Get colors for a tier (convenience function).
 */
export function getTierColors(tier: ScoreTier): TierColors {
  return SCORE_TIER_COLORS[tier];
}

/**
 * Get colors for a grade (convenience function).
 */
export function getGradeColors(grade: Grade): TierColors {
  return SCORE_TIER_COLORS[gradeToTier(grade)];
}

/**
 * Get colors for a percentage score (convenience function).
 */
export function getPercentageColors(score: number): TierColors {
  return SCORE_TIER_COLORS[percentageToTier(score)];
}
